import { useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'ui';
import { WholesaleDeliveryCondition } from 'public/context/CartContext';
import { CartProductForDelivery } from '../types';
import DeliveryCard, { DeliveryCardRef } from './DeliveryCard';

interface DetailedDeliveryProps {
  deliveries: WholesaleDeliveryCondition[];
  onDeliveriesChange: (
    deliveries:
      | WholesaleDeliveryCondition[]
      | ((prev: WholesaleDeliveryCondition[]) => WholesaleDeliveryCondition[])
  ) => void;
  cartProducts: CartProductForDelivery[];
}

export interface DetailedDeliveryRef {
  validateAll: () => Promise<boolean>;
}

const DetailedDelivery = forwardRef<DetailedDeliveryRef, DetailedDeliveryProps>(({
  deliveries,
  onDeliveriesChange,
  cartProducts,
}, ref) => {
  const intl = useIntl();
  const cardRefs = useRef<Map<string, DeliveryCardRef>>(new Map());

  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const validations = Array.from(cardRefs.current.values()).map((cardRef) =>
        cardRef.validateFields().then(() => true).catch(() => false)
      );
      const results = await Promise.all(validations);
      return results.every((result) => result === true);
    },
  }));

  const calculateUnusedProducts = useMemo(() => {
    const usedQuantities: Record<number, number> = {};

    deliveries.forEach((delivery) => {
      delivery.products?.forEach((product) => {
        usedQuantities[product.product_id] =
          (usedQuantities[product.product_id] || 0) + product.quantity;
      });
    });

    return cartProducts.map((product) => ({
      ...product,
      remainingQuantity: product.quantity - (usedQuantities[product.product_id] || 0),
    }));
  }, [deliveries, cartProducts]);

  const handleAddDelivery = () => {
    const newDelivery: WholesaleDeliveryCondition = {
      id: crypto.randomUUID(),
      delivery_address: '',
      products: [],
    };
    onDeliveriesChange([...deliveries, newDelivery]);
  };

  const handleUpdateDelivery = useCallback(
    (updatedDelivery: WholesaleDeliveryCondition) => {
      onDeliveriesChange((prev) =>
        prev.map((d) => (d.id === updatedDelivery.id ? updatedDelivery : d))
      );
    },
    [onDeliveriesChange]
  );

  const handleRemoveDelivery = (id: string) => {
    onDeliveriesChange(deliveries.filter((d) => d.id !== id));
  };

  const getAvailableProductsForDelivery = (deliveryId: string) => {
    const currentDelivery = deliveries.find((d) => d.id === deliveryId);
    const currentDeliveryProducts = currentDelivery?.products || [];
    const currentProductIds = new Set(currentDeliveryProducts.map((p) => p.product_id));

    return calculateUnusedProducts
      .map((product) => {
        const currentlyAssigned =
          currentDeliveryProducts.find((p) => p.product_id === product.product_id)?.quantity || 0;
        return {
          ...product,
          availableQuantity: product.remainingQuantity + currentlyAssigned,
        };
      })
      .filter(
        (product) => product.availableQuantity > 0 || currentProductIds.has(product.product_id)
      );
  };

  return (
    <div className="detailed-delivery-container">
      {deliveries.map((delivery, index) => (
        <DeliveryCard
          key={delivery.id}
          ref={(el) => {
            if (el) cardRefs.current.set(delivery.id, el);
            else cardRefs.current.delete(delivery.id);
          }}
          delivery={delivery}
          index={index}
          availableProducts={getAvailableProductsForDelivery(delivery.id)}
          onUpdate={handleUpdateDelivery}
          onRemove={() => handleRemoveDelivery(delivery.id)}
          canRemove={deliveries.length > 1}
        />
      ))}

      <Button
        onClick={handleAddDelivery}
        label={intl.formatMessage({ id: 'wholesale.delivery.add_condition' })}
        className="add-delivery-btn"
      />
    </div>
  );
});

export default DetailedDelivery;
